import { QuoteSide } from "@/lib/interfaces and Enums/ENUMQuoteSide";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";
import { getBestCounterparty } from "../actionSummaryMainTableColDefs";
import { getQuoteTypeFromBlockId } from "../colDefs/colDefsShared";
import {
  returnFractionStringQuoteValue,
  returnFractionStringZValueDecimal,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { determineNotationCurrencySymbol } from "@/lib/currencySymbolHelper";
import {
  returnDecimalFormatting,
  isPriceNotation,
} from "@/lib/globalFormatterHelper";
import { findCPOnTrade } from "../tradeLookupHelpers";

export function formattedQuote(params: any, thisTrade?: any) {
  const data = params.data;
  const tableLevel = params.tableLevel;
  const fullTradeData = params.fullTradeData;

  let quoteType;
  let quoteSide;
  let notation: NotationToDecimalValueType | undefined;
  let field;

  let isOutright = false;
  if (fullTradeData.strategyType == StrategyType.Outright) isOutright = true; // 15063 - Bug with outrights not correctly identified.
  // USE this to determine leg or ER? - params.data.legs != null AND strategyType == outright.

  if (params.colDef.field == "bid") {
    field = QuoteSide.Bid;
  } else if (params.colDef.field == "ask") {
    field = QuoteSide.Ask;
  } else {
    field = QuoteSide.Mid;
  }

  if (tableLevel == "ES") {
    const bestCounterparty = getBestCounterparty(thisTrade ?? params.data);

    if (bestCounterparty?.legs?.length > 0) {
      quoteType = bestCounterparty.quoteType;
      quoteSide = isOutright
        ? bestCounterparty.legs[0].quoteSide
        : bestCounterparty.quoteSideER;

      notation = isOutright
        ? bestCounterparty.legs[0].notation
        : fullTradeData.role == "OP"
          ? bestCounterparty.notation
          : bestCounterparty.notationER;
    } else {
      quoteSide = fullTradeData.quoteSideES;
      notation = fullTradeData.notationES;
    }
  } else if (tableLevel == "ER") {
    if (fullTradeData.role == "OP") {
      const liveER = findCPOnTrade(thisTrade, data.executionRequestId);
      quoteType = liveER?.quoteType ?? data.quoteType;
      quoteSide = isOutright ? data.legs?.[0]?.quoteSide : data.quoteSideER;
      notation = isOutright
        ? data.legs?.[0]?.notation
        : data.notation;
    } else {
      // RP: the ER row IS the trade (returnRowDataAtDetailTable passes [params.data]),
      // so quoteType / quoteSide / notation live directly on thisTrade. AG Grid never
      // re-fetches the detail row data, so reading from `data` would be stale after
      // the OP toggles Two-Way ↔ One-Way and sends — falling back to `data` only when
      // the store has no live trade yet.
      quoteType = thisTrade?.quoteType ?? data.quoteType;
      quoteSide = isOutright
        ? thisTrade?.legs?.[0]?.quoteSide ?? data.legs?.[0]?.quoteSide
        : thisTrade?.quoteSideER ?? data.quoteSideER;
      notation = isOutright
        ? thisTrade?.legs?.[0]?.notation ?? data.legs?.[0]?.notation
        : thisTrade?.notationER ?? data.notationER;
    }
  } else if (tableLevel == "Leg") {
    const liveTrade = thisTrade ?? fullTradeData;
    quoteType =
      fullTradeData.role == "OP"
        ? getQuoteTypeFromBlockId(liveTrade, params.data.blockId)
        : liveTrade.quoteType;
    if (fullTradeData.role == "OP") {
      quoteSide = data.quoteSide;
      notation = data.notation;
    } else {
      // RP: detail row data is stale after OP-side Two-Way ↔ One-Way send, so look
      // up the live leg in the store by blockId.
      const liveLeg = thisTrade?.legs?.find(
        (l: any) => l.blockId === params.data.blockId,
      );
      quoteSide = liveLeg?.quoteSide ?? data.quoteSide;
      notation = liveLeg?.notation ?? data.notation;
    }
  }

  // Determine the display symbol for notation (replaces "$" with currency symbol at ES/ER levels)
  const notationDisplaySymbol =
    (tableLevel === "ES" || tableLevel === "ER") && notation === "$"
      ? determineNotationCurrencySymbol(fullTradeData, notation)
      : notation;

  // Currency notations ($ and resolved symbols like £, €, ¥) display BEFORE the number.
  // Non-currency notations (%, bp, $fr, $p) display AFTER the number.
  const isCurrencyNotation = notation === "$";

  let quoteDisplayValue = params.value; // Will be used to show the field value in the JSX return.
  const isDollarFraction = notation === "$fr";

  // Have a null variable that's only used when the notaion is $fr.
  let zValueDecimal = <span></span>;
  // Replace the null with a span tag that has a fractional dispaly in it that represents the last character.
  if (isDollarFraction) {
    // Get updated values from helper functions
    quoteDisplayValue = returnFractionStringQuoteValue(isOutright, params);
    zValueDecimal = returnFractionStringZValueDecimal(isOutright, params);
  }

  const NPVPlaceHolder = (
    <div className="mr-1 flex h-full items-center text-ellipsis pl-1 text-gray-300 dark:text-zinc-500">
      NPV
    </div>
  );

  function returnValueWithNotation() {
    // Early return for NPV
    if (params.data.unwind == true) {
      return NPVPlaceHolder;
    }

    // For currency notations, negative values display as -$1.00 not $-1.00
    const isNegative = isCurrencyNotation && Number(params.value) < 0;

    let quoteDisplayJSX = (
      <div className="mr-1 flex h-full items-center text-ellipsis pl-1">
        {isCurrencyNotation && (
          <>
            {isNegative && "-"}
            {notationDisplaySymbol}
          </>
        )}
        {isCurrencyNotation
          ? String(quoteDisplayValue).replace(/^-/, "")
          : quoteDisplayValue}
        {zValueDecimal}
        {!isCurrencyNotation && !isPriceNotation(notation) && (
          <> {notationDisplaySymbol}</>
        )}
      </div> // ABOVE - If there is a string value in the zValue, don't convert the quoteDisplayValue.
    );

    // Allowing negative values to round by taking the first value off the string. Without 'substring', negative values getting treated as a dollar fraction.
    if (!params.value.toString().substring(1).includes("-")) {
      // if there is no zValue safe to assume there is no - in the stirng.
      const formattedValue = returnDecimalFormatting(
        Number(quoteDisplayValue),
        notation,
      );
      // Preserve a user-entered leading "-" that numeric formatting drops (e.g. "-0" → 0 → "0.00").
      // Display must match what the user typed so they can see the value being validated.
      const originalHasLeadingMinus = String(params.value).trim().startsWith("-");
      const formattedWithSign =
        originalHasLeadingMinus && !String(formattedValue).trim().startsWith("-")
          ? `-${formattedValue}`
          : formattedValue;
      quoteDisplayJSX = (
        <div className="mr-1 flex h-full items-center text-ellipsis pl-1">
          {isCurrencyNotation && (
            <>
              {isNegative && "-"}
              {notationDisplaySymbol}
            </>
          )}
          {isCurrencyNotation
            ? String(formattedWithSign).replace(/^-/, "")
            : formattedWithSign}
          {!isCurrencyNotation && !isPriceNotation(notation) && (
            <> {notationDisplaySymbol}</>
          )}
        </div> // ABOVE - Convert when not using a fraction.
      );
    }
    return quoteDisplayJSX;
  }

  const notationDisplay = (
    <div className="mr-1 flex h-full items-center text-ellipsis pl-1 text-[#007dc0] dark:text-[#37b5f0]">
      {isPriceNotation(notation) ? "price" : notationDisplaySymbol}
    </div>
  );

  if (
    (quoteType == "One-Way" && quoteSide == field) ||
    (quoteType == "Two-Way" && field != "Mid") ||
    (quoteType == "Mid" && field == "Mid")
  ) {
    if (
      params.value != null &&
      params.value != "" &&
      params.value != undefined
    ) {
      return returnValueWithNotation();
    }

    if (params.data.unwind == true) {
      return NPVPlaceHolder;
    }
    // last catch all - Grayed out notation if no value and NON NPV
    return notationDisplay;
  } else {
    if (params.value) {
      return returnValueWithNotation();
    } else if (field == quoteSide && !params.value) return notationDisplay;
    return <div></div>; // show nothing if it's not the side of the trade AND there is no value.
  }
}
